import SuggestionsDropdown from '@finn-no/suggestions-dropdown';
import el from '@finn-no/create-element';

export default {
    dependencies: [
        '$element',
        'trackingservice',
        'geoservice',
        'radio',
        'xhrservice',
    ],
    callable(ele, trackingService, geoservice, radio, xhr) {
        const radiusCheckboxEle = ele.querySelector(
            '[data-geo-filter-checkbox]'
        );
        const geoCenterInput = ele.querySelector('[data-geo-center]');
        const errorMsgEle = ele.querySelector('[data-error-message]');
        const searchEle = ele.querySelector('[data-geo-search]');
        const vertical = ele.getAttribute('data-vertical');

        const latEle = ele.querySelector("[name='lat']");
        const lonEle = ele.querySelector("[name='lon']");

        const mapApiUrl = ele.getAttribute('data-map-api-url');
        const isFullscreen = ele.getAttribute('data-fullscreen') === 'true';
        const MAX_NUMBER_OF_LOCATIONS = 5;

        let isLocationChanged = false;
        let isLocationSuggested = false;
        let defaultLocationsForDropDown = [];

        /*
         * Initialization logic
         */

        if (!isFullscreen) {
            radio('geo-position-changed-from-fullscreen-search').subscribe(
                handleGeoSearchSuccess
            );
            radio('geo-radius-changed-from-filters').subscribe(
                handleRadiusChanged
            );
            radio('geo-position-changed-from-sort').subscribe(
                handleSortLocationChanged
            );
            radio('geo-map-clicked').subscribe(handleMapClicked);
        }

        getLastLocations();

        function getLastLocations() {
            geoservice
                .getLastLocations(vertical)
                .then(locations =>
                    Promise.all(locations.map(extractLocationPromise))
                )
                .then(locationsWithName => {
                    defaultLocationsForDropDown = locationsWithName;
                    if (
                        !geoCenterInput.value &&
                        latEle.value &&
                        !isFullscreen
                    ) {
                        setInitialGeoCenterInput();
                    } else if (!latEle.value && locationsWithName.length > 0) {
                        setInitialLocation(locationsWithName[0]);
                    }
                });
        }

        function setInitialGeoCenterInput() {
            const selectedLocations = defaultLocationsForDropDown.filter(
                location =>
                    location.latitude === latEle.value &&
                    location.longitude === lonEle.value
            );

            if (selectedLocations.length > 0) {
                addLocationToGeoCenterInput(selectedLocations[0].name);
                sendLocationNameChangedEvent();
            } else {
                findLocationName(
                    latEle.value,
                    lonEle.value
                ).then(locationName => {
                    addLocationToGeoCenterInput(locationName);
                    sendLocationNameChangedEvent();
                    addLocationToDropdown(
                        latEle.value,
                        lonEle.value,
                        locationName
                    );
                });
            }
        }

        function sendLocationNameChangedEvent() {
            if (radiusCheckboxEle.checked) {
                radio('geo-location-name-changed').broadcast();
            }
        }

        function setInitialLocation(location) {
            isLocationSuggested = location.suggested === 'true';
            latEle.value = location.latitude || '';
            lonEle.value = location.longitude || '';
            radiusCheckboxEle.value =
                location.radius || radiusCheckboxEle.value;
            sendLocationChangedEvent(
                location.latitude,
                location.longitude,
                location.radius
            );
            addLocationToGeoCenterInput(location.name);
        }

        function sendChangeEvent(element) {
            const changeEvent = document.createEvent('Event');
            changeEvent.initEvent('change', true, true);
            element.dispatchEvent(changeEvent);
        }

        function extractLocationPromise(location) {
            const namePromise = location.name
                ? Promise.resolve(location.name)
                : findLocationName(location.lat, location.lon);
            return namePromise.then(name => ({
                longitude: location.lon,
                latitude: location.lat,
                name,
                suggested: location.suggested,
                radius: location.radius,
            }));
        }

        function handleRadiusChanged(radius) {
            if (radiusCheckboxEle.value !== radius) {
                radiusCheckboxEle.value = radius;
                radiusCheckboxEle.checked = true;
                toggleGeoMapShading();
                filterResultsByGeoLocation();

                trackClick();
            }
        }

        function handleSortLocationChanged(lat, lon) {
            latEle.value = lat || '';
            lonEle.value = lon || '';

            if (lat && lon) {
                findLocationName(lat, lon).then(locationName => {
                    addLocationToDropdown(lat, lon, locationName);
                    addLocationToGeoCenterInput(locationName);
                });
            }
            radiusCheckboxEle.checked = true;
            toggleGeoMapShading();

            sendLocationChangedEvent(lat, lon);
        }

        function handleMapClicked() {
            trackMapClick();
        }

        function findLocationName(lat, lon) {
            return geoservice.getLocationName(mapApiUrl, lat, lon);
        }

        function filterResultsByGeoLocation() {
            if (
                latEle.value &&
                lonEle.value &&
                geoCenterInput.value &&
                radiusCheckboxEle.value
            ) {
                geoservice.saveLocation(
                    latEle.value,
                    lonEle.value,
                    geoCenterInput.value,
                    radiusCheckboxEle.value,
                    vertical
                );
            }
            radio('geoSlider.searched').broadcast();

            const searchForm = ele.closest('form');
            if (!searchForm) {
                return;
            }
            sendChangeEvent(searchForm);
        }

        /*
         * Geo search UI logic
         */

        const options = {
            dropdownClass: 'man',
            selectedClass: 'bg-ice',
            minimumTermLength: 0,
            onsubmit: submitSelectedLocation,
            onshow: highlightFirstDropdownItem,
        };

        const suggestionsDropdown = new SuggestionsDropdown(
            geoCenterInput,
            fetchSuggestions,
            abortRequest,
            options
        );

        radiusCheckboxEle.addEventListener(
            'change',
            handleRadiusCheckboxChange
        );
        geoCenterInput.addEventListener('focus', hideErrorMessage);
        geoCenterInput.addEventListener('focus', clearInputAndShowSuggestions);
        geoCenterInput.addEventListener('blur', restoreInputValue);

        function submitSelectedLocation(item, itemEle) {
            ele.querySelector('[data-focus]').focus();

            if (!itemEle) {
                handleGeoSearchFailure();
                return;
            }

            const lat = geoservice.round(itemEle.getAttribute('data-latitude'));
            const lon = geoservice.round(
                itemEle.getAttribute('data-longitude')
            );

            handleGeoSearchSuccess(
                Number(lat),
                Number(lon),
                itemEle.textContent
            );

            sendChangeEvent(geoCenterInput);
        }

        function highlightFirstDropdownItem() {
            suggestionsDropdown.highlightItem(0);
        }

        let canceler;

        function fetchSuggestions(term, onsuccess) {
            abortRequest();
            if (term.length > 1) {
                canceler = xhr.canceler();

                xhr
                    .request({
                        url: `${mapApiUrl}/geo/query/solr.json`,
                        data: {
                            type: 'GAB,PLACE',
                            limit: MAX_NUMBER_OF_LOCATIONS,
                            q: term,
                            lat: latEle.value,
                            lng: lonEle.value,
                        },
                        type: 'json',
                        canceler,
                    })
                    .then(result => {
                        if (!result || result.locations.length === 0) {
                            suggestionsDropdown.hide();
                            return;
                        }

                        return onsuccess(renderDropdown(result.locations));
                    });
            } else if (defaultLocationsForDropDown.length > 0) {
                return onsuccess(renderDropdown(defaultLocationsForDropDown));
            } else {
                return null;
            }
        }

        function abortRequest() {
            if (canceler) {
                canceler();
            }
        }

        function renderDropdown(items) {
            const listElement = isFullscreen
                ? 'ul.mhn.dropdown.largetext'
                : 'ul.man.dropdown';
            const itemElement = isFullscreen
                ? 'li.dropdown-item.pam.clickable'
                : 'li.dropdown-item.phm.pvs.clickable';

            return el(
                listElement,
                null,
                items.map(item =>
                    el(
                        itemElement,
                        {
                            'data-longitude': item.longitude,
                            'data-latitude': item.latitude,
                        },
                        item.name
                    )
                )
            );
        }

        function handleGeoSearchSuccess(lat, lon, locationName) {
            if (lat && lon) {
                hideErrorMessage();
                addLocationToDropdown(lat, lon, locationName);
                addLocationToGeoCenterInput(locationName);
                radiusCheckboxEle.checked = true;
            } else {
                removeLocationFromGeoCenterInput();
                radiusCheckboxEle.checked = false;
            }

            latEle.value = lat || '';
            lonEle.value = lon || '';
            if (!isFullscreen) {
                filterResultsByGeoLocation();
                toggleGeoMapShading();

                isLocationChanged = true;
                if (radiusCheckboxEle.checked) {
                    trackClick();
                }
            }
            sendLocationChangedEvent(lat, lon);
        }

        function sendLocationChangedEvent(lat, lon, radius) {
            if (isFullscreen) {
                radio('geo-position-changed-from-filters-fullscreen').broadcast(
                    lat,
                    lon,
                    radius
                );
            } else {
                radio('geo-position-changed-from-filters').broadcast(
                    lat,
                    lon,
                    radius
                );
            }
        }

        function handleGeoSearchFailure() {
            if (geoCenterInput.value) {
                showErrorMessage(
                    'Vi finner ikke denne adressen eller stedet. Sjekk om det er riktig.'
                );
            }

            sendLocationChangedEvent();
        }

        let currentValue;
        function clearInputAndShowSuggestions() {
            if (defaultLocationsForDropDown.length > 0) {
                suggestionsDropdown.show(
                    renderDropdown(defaultLocationsForDropDown)
                );
            }

            currentValue = geoCenterInput.value;
            geoCenterInput.value = '';
        }

        function restoreInputValue() {
            if (!geoCenterInput.value) {
                geoCenterInput.value = currentValue || '';
            }
        }

        function handleRadiusCheckboxChange() {
            if (!latEle.value && !lonEle.value) {
                showErrorMessage(
                    'Du må finne en lokasjon for å kunne filtrere på område.'
                );
                return;
            }
            filterResultsByGeoLocation();
            toggleGeoMapShading();

            sendLocationChangedEvent(latEle.value, lonEle.value);
            if (radiusCheckboxEle.checked) {
                trackClick();
            }
        }

        function toggleGeoMapShading() {
            if (radiusCheckboxEle) {
                const geoMap = ele.querySelector('[data-geo-map]');
                geoMap.style.opacity = radiusCheckboxEle.checked ? 1.0 : 0.7;
            }
        }

        /*
         * Presentation logic
         */

        function addLocationToDropdown(lat, lon, name) {
            if (lat && lon && name) {
                const locationAlreadyAdded = defaultLocationsForDropDown.filter(
                    entry => entry.name === name
                );
                if (locationAlreadyAdded.length === 0) {
                    defaultLocationsForDropDown.unshift({
                        longitude: lon,
                        latitude: lat,
                        name,
                    });
                    defaultLocationsForDropDown = defaultLocationsForDropDown.slice(
                        0,
                        MAX_NUMBER_OF_LOCATIONS
                    );
                }
            }
        }

        function addLocationToGeoCenterInput(locationName) {
            geoCenterInput.value = locationName;
        }

        function removeLocationFromGeoCenterInput() {
            geoCenterInput.value = '';
        }

        function showErrorMessage(msg) {
            errorMsgEle.innerText = msg;
            errorMsgEle.classList.remove('hidden');
            searchEle.classList.add('invalid');
        }

        function hideErrorMessage() {
            searchEle.classList.remove('invalid');
            errorMsgEle.classList.add('hidden');
            errorMsgEle.innerText = '';
        }

        /*
         * Tracking logic
         */

        function trackClick() {
            trackingService.trackEventObject({
                analyticsName: 'navigation.filter-by-georadius.click',
                // eslint-disable-next-line no-nested-ternary
                stringValue: isLocationSuggested
                    ? isLocationChanged
                      ? 'suggested-location-changed'
                      : 'suggested-location'
                    : null,
                numberValue: radiusCheckboxEle.value,
            });
        }

        function trackMapClick() {
            trackingService.trackEventObject({
                analyticsName: 'navigation.filter-by-georadius.map-click',
            });
        }
    },
};



// WEBPACK FOOTER //
// ./controllers/geoWidget.js